/**
 * CoachMessageBubble — bolla di messaggio del coach (user / assistant).
 * Estratto da WeeklyReviewChat.jsx per essere riusato da WeeklyPlanChat.
 * Comportamento invariato: gestisce divisori, messaggi utente, messaggi
 * assistant con markdown, quick-reply chips, banner "Sto per salvare" con
 * conferma/annulla/modifica, riassunto "Salvato" e stato "Annullato".
 */
import { Loader2, Check, X, Sparkles, AlertTriangle } from 'lucide-react';
import CoachMarkdown from '../CoachMarkdown';
import OpFieldsList from './OpFieldsList';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';

const ENTITA_LABEL = {
  anno: 'Anno', numero_importante: 'Numero importante', obiettivo: 'Obiettivo',
  key_result: 'Key Result', target_periodo: 'Target di periodo', azione: 'Azione settimanale',
  mit: 'MIT', review: 'Review', capacita: 'Capacità', revisione: 'Revisione',
};
const AZIONE_LABEL = { crea: 'Crea', aggiorna: 'Aggiorna', chiudi: 'Chiudi' };

export default function CoachMessageBubble({
  message, isPending, editingJson, setEditingJson, applying,
  onConfirm, onCancel, onEdit, onChipClick = null, aree = [],
}) {
  if (message.isDivider) {
    return (
      <div className="flex items-center gap-3 my-4 select-none" data-testid="msg-divider" aria-hidden="true">
        <div className="flex-1 h-px bg-line" />
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-mute whitespace-nowrap">
          {(message.content || '').replace(/—/g, '').trim() || 'Sezione precedente'}
        </span>
        <div className="flex-1 h-px bg-line" />
      </div>
    );
  }
  if (message.role === 'user') {
    return (
      <div className="flex justify-end" data-testid="msg-user">
        <div className="max-w-[80%] bg-ink text-canvas px-5 py-3 font-body text-sm leading-relaxed">{message.content}</div>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="msg-assistant">
      <div className="flex items-baseline gap-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent mt-1">QuantumCoach</span>
        <div className="flex-1 min-w-0">
          <CoachMarkdown content={message.content} aree={aree} />
        </div>
      </div>
      {!isPending && !message.applied && !message.cancelled && Array.isArray(message.suggerimenti) && message.suggerimenti.length > 0 && onChipClick && (
        <div className="ml-[58px] flex flex-wrap gap-2" data-testid="quick-replies">
          {message.suggerimenti.map((s, i) => (
            <button
              key={`${i}-${s}`}
              type="button"
              onClick={() => onChipClick(s)}
              className="border border-line hover:border-accent hover:bg-accent/5 px-3 py-1.5 font-mono text-[11px] text-soft hover:text-accent transition-colors"
              data-testid={`quick-reply-${i}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
      {message.operazioni && message.operazioni.length > 0 && (
        <div className="ml-[58px] border-l-2 border-accent pl-4 py-2 bg-accent/5" data-testid="pending-operations">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={12} strokeWidth={1.5} className="text-accent" />
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-accent">Sto per salvare</span>
          </div>
          {editingJson !== null ? (
            <Textarea value={editingJson} onChange={(e) => setEditingJson(e.target.value)}
              className="rounded-none font-mono text-xs border-line focus-visible:ring-0 focus-visible:border-accent min-h-[200px] bg-paper"
              data-testid="ops-edit-json" />
          ) : (
            <ul className="space-y-2 mb-4">
              {message.operazioni.map((op, i) => (
                <li key={op.id || `${op.entita}-${op.azione}-${i}`} className="font-mono text-xs leading-relaxed" data-testid="pending-op-item">
                  <span className="inline-block bg-ink text-canvas px-1.5 py-0.5 mr-2 uppercase tracking-wider text-[10px]">{AZIONE_LABEL[op.azione] || op.azione}</span>
                  <span className="text-soft">{ENTITA_LABEL[op.entita] || op.entita}</span>
                  {op.id && <span className="text-mute"> · {op.id.slice(0, 8)}…</span>}
                  <OpFieldsList campi={op.campi} />
                </li>
              ))}
            </ul>
          )}
          {isPending && (
            <div className="flex gap-2 items-center pt-2">
              <Button onClick={onConfirm} disabled={applying} className="rounded-none bg-ink hover:bg-accent text-canvas font-mono text-xs uppercase tracking-[0.2em] h-8 px-4" data-testid="confirm-ops-btn">
                {applying ? <Loader2 size={12} className="animate-spin mr-2" /> : <Check size={12} strokeWidth={1.8} className="mr-2" />}
                Conferma
              </Button>
              <button onClick={onCancel} disabled={applying} className="font-mono text-xs uppercase tracking-[0.2em] text-soft hover:text-ink px-3 h-8 flex items-center" data-testid="cancel-ops-btn">
                <X size={12} strokeWidth={1.8} className="mr-2" /> Annulla
              </button>
              <button onClick={onEdit} disabled={applying || editingJson !== null} className="font-mono text-xs uppercase tracking-[0.2em] text-soft hover:text-ink px-3 h-8 flex items-center disabled:opacity-40" data-testid="edit-ops-btn">
                Modifica
              </button>
            </div>
          )}
        </div>
      )}
      {message.applied && (
        <div className="ml-[58px] border-l-2 border-ok pl-4 py-2 bg-ok/10" data-testid="applied-summary">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ok mb-2"><Check className="inline h-3 w-3 mr-1" strokeWidth={1.5} /> Salvato</div>
          {Array.isArray(message.appliedSummary) && message.appliedSummary.length > 0 && (
            <ul className="space-y-1">
              {message.appliedSummary.map((s, i) => (
                <li key={`${i}-${s}`} className="font-body text-xs text-ink">{s}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {message.cancelled && (
        <div className="ml-[58px] font-mono text-[10px] uppercase tracking-[0.2em] text-mute">
          <AlertTriangle className="inline h-3 w-3 mr-1" strokeWidth={1.5} /> Annullato
        </div>
      )}
    </div>
  );
}
